<script>
import autosize from 'autosize';
import { mapMutations, mapState } from 'vuex';
import AppCheckbox from '@/common/AppCheckbox.vue';
import AppTooltip from '@/common/AppTooltip.vue';
import AppIcon from '@/common/AppIcon.vue';

export default {
  name: 'DedicationForm',
  components: {
    AppCheckbox,
    AppTooltip,
    AppIcon
  },
  data() {
    return {
      maxLength: 150
    };
  },
  computed: {
    ...mapState('campaign', ['settings']),

    dedicationForm: {
      get() {
        return (
          this.donation.dedication || {
            message: '',
            public: false,
            notifyByEmail: false,
            email: ''
          }
        );
      },
      set(value) {
        this.SET_DEDICATION(value);
      }
    },

    donation() {
      return this.$store.state.donation;
    },

    characterCount() {
      return this.dedicationForm.message ? this.dedicationForm.message.length : 0;
    },

    helpText() {
      return `${this.characterCount} / ${this.maxLength}`;
    },

    // Custom labels for different campaigns
    IS_MEIMASAI_DEFAULT_CAMPAIGN() {
      return this.settings?.id === 'f4b200a0-bf71-11ee-bd71-99b6de87b26d';
    },

    dedicationFieldsLabels() {
      if (this.IS_MEIMASAI_DEFAULT_CAMPAIGN) {
        return {
          public: 'Display in public',
          notifyByEmail: 'Notify someone via email',
          email: 'Honoree email',
          message: 'Message to honoree'
        };
      }

      return {
        public: 'Make it public',
        notifyByEmail: 'Notify someone via email',
        email: this.$t('additionalOptions.dedicateeEmail'),
        message: this.$t('additionalOptions.message')
      };
    },

    emailValidation() {
      return this.dedicationForm.notifyByEmail ? '^required|email' : '';
    }
  },

  methods: {
    async validate() {
      this.$refs.hiddenSubmitButton?.click();
      await this.$nextTick();
      const hasErrors = await this.$refs.dedicationFormRef?.hasValidationErrors();
      return !hasErrors;
    },

    ...mapMutations('donation', ['SET_DEDICATION']),

    updatePublic(value) {
      this.dedicationForm = {
        ...this.dedicationForm,
        public: value
      };
    },

    updateNotifyByEmail(value) {
      this.dedicationForm = {
        ...this.dedicationForm,
        notifyByEmail: value,
        email: value ? this.dedicationForm.email : ''
      };
    },

    resize(update = false) {
      const textarea = this.$refs.textarea.$el.querySelector('textarea');
      if (!textarea) return;

      if (update) {
        return autosize.update(textarea);
      }

      autosize(textarea);
    }
  },

  mounted() {
    this.$nextTick(() => this.resize());
  },

  beforeDestroy() {
    const textarea = this.$refs.textarea?.$el?.querySelector('textarea');
    if (textarea) autosize.destroy(textarea);
  }
};
</script>

<template>
  <formulate-form ref="dedicationFormRef" class="dedication-form flex flex-col gap-4 w-full">
    <formulate-input
      type="textarea"
      ref="textarea"
      v-model="dedicationForm.message"
      :maxlength="maxLength"
      validation="required"
      validation-name="Dedication message"
      @input.native="resize()"
      @focusin.native="resize(true)"
      @focusout.native="resize(true)"
      style="min-height: 84px"
      placeholder="Your dedication message"
      :help="helpText"
    />

    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-1.5">
        <app-checkbox :value="dedicationForm.public" @input="updatePublic" size="18.01">
          {{ dedicationFieldsLabels.public }}
        </app-checkbox>
        <app-tooltip>
          <app-icon name="info" type="svg" width="14" height="14" class="text-black fill-current" />
          <template #content>
            <p>Your donation may appear on the donation feed</p>
          </template>
        </app-tooltip>
      </div>

      <!-- Notify by email -->
      <app-checkbox :value="dedicationForm.notifyByEmail" @input="updateNotifyByEmail" size="18.01">
        {{ dedicationFieldsLabels.notifyByEmail }}
      </app-checkbox>

      <!-- Email field (conditional) -->
      <transition name="expandx2">
        <div v-show="dedicationForm.notifyByEmail" class="ml-3">
          <formulate-input
            input-class="bg-white"
            type="email"
            name="email"
            v-model="dedicationForm.email"
            :validation="emailValidation"
            :validation-name="dedicationFieldsLabels.email"
            :placeholder="dedicationFieldsLabels.email"
          />
        </div>
      </transition>
    </div>
    <button ref="hiddenSubmitButton" type="submit" class="hidden"></button>
  </formulate-form>
</template>
